import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  addParentChild,
  getPublicPlans,
  getParentDiscountStatus,
  getParentPendingInvitations,
  type PublicPlan,
  type ParentPendingInvitation,
} from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameCard, GameHero } from "../../../components/game";
import {
  athleteAges,
  isValidAthleteAge,
  MAX_CHILD_AGE,
  MIN_ATHLETE_AGE,
} from "../../../lib/athleteAge";

type IdMode = "username" | "email";

export default function AppParentAddChild() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [paidCount, setPaidCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [age, setAge] = useState("");
  const [idMode, setIdMode] = useState<IdMode>("username");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [planCode, setPlanCode] = useState("free");
  const [billing, setBilling] = useState<"month" | "year">("month");

  /**
   * A club invitation, if this parent arrived from one.
   *
   * When it is set, the child's club and team are already decided and the plan
   * is the club's — so the plan picker disappears entirely. Asking a parent to
   * choose and pay for a plan their club already provides is the wrong
   * question.
   */
  const [invitation, setInvitation] = useState<ParentPendingInvitation | null>(null);
  const invitationToken = searchParams.get("invitation");

  useEffect(() => {
    (async () => {
      try {
        const [p, d, invites] = await Promise.all([
          getPublicPlans(),
          getParentDiscountStatus(),
          getParentPendingInvitations().catch(() => [] as ParentPendingInvitation[]),
        ]);
        setPlans(p);
        setPaidCount(d.paid_count);

        // Prefer the invitation named in the URL; otherwise, if the club is
        // waiting on exactly one child, use that — the parent should not have
        // to know a token to finish what the club started.
        const fromUrl = invitationToken ? invites.find((i) => i.token === invitationToken) : null;
        const chosen = fromUrl || (invites.length === 1 ? invites[0] : null);
        if (chosen) {
          setInvitation(chosen);
          if (chosen.athlete_name) setFirstName(chosen.athlete_name);
          setPlanCode("free");
        } else {
          // Default to the first paid plan if any, else free.
          const firstPaid = p.find((x) => !x.is_free);
          setPlanCode(firstPaid ? firstPaid.code : p[0]?.code || "free");
        }
      } catch {
        /* non-fatal */
      } finally {
        setLoading(false);
      }
    })();
  }, [invitationToken]);

  const selectedPlan = useMemo(() => plans.find((p) => p.code === planCode) || null, [plans, planCode]);
  const isPaid = !invitation && !!selectedPlan && !selectedPlan.is_free;

  // What tier the parent reaches by adding THIS paid child.
  const projectedPaid = paidCount + (isPaid ? 1 : 0);
  const projectedDiscount = projectedPaid >= 3 ? 30 : projectedPaid === 2 ? 20 : 0;

  const money = (cents: number, currency: string) =>
    `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: cents % 100 ? 2 : 0 })} ${currency}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim()) return setError(t("addChild.err_name", "Numele copilului este obligatoriu."));
    const ageNum = parseInt(age, 10);
    if (!isValidAthleteAge(ageNum, MAX_CHILD_AGE)) {
      return setError(
        t("addChild.err_age", "Vârsta copilului trebuie să fie între {{min}} și {{max}} ani.", {
          min: MIN_ATHLETE_AGE,
          max: MAX_CHILD_AGE,
        })
      );
    }
    if (idMode === "username" && !username.trim()) return setError(t("addChild.err_username", "Alege un username pentru copil."));
    if (idMode === "email" && !email.trim()) return setError(t("addChild.err_email", "Introdu un email pentru copil."));
    if (password.length < 6) return setError(t("addChild.err_password", "Parola trebuie să aibă minim 6 caractere."));

    setSaving(true);
    try {
      const res = await addParentChild({
        first_name: firstName.trim(),
        age: ageNum,
        username: idMode === "username" ? username.trim() : undefined,
        email: idMode === "email" ? email.trim() : undefined,
        password,
        plan: planCode,
        billing_period: billing,
        invitation_token: invitation?.token,
      });
      if (res.checkout_url) {
        // Paid plan → go pay. Stripe redirects back to the dashboard on success.
        window.location.href = res.checkout_url;
        return;
      }
      if (res.checkout_error) {
        setError(res.checkout_error);
        setSaving(false);
        return;
      }
      // Free plan (or paid without checkout) → back to the dashboard.
      navigate(`/app/dashboard?child=${res.child.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || t("addChild.err_generic", "Nu am putut adăuga copilul."));
      setSaving(false);
    }
  };

  return (
    <MobileAppLayout>
      <div className="pb-8">
        {/* Header — shared dark hero, matching the other parent screens. */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <div className="flex items-center gap-2.5">
            <Link to="/app/dashboard" className="-ml-1.5 p-1.5 rounded-full hover:bg-white/10 transition-colors shrink-0">
              <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-extrabold truncate">{t("addChild.title", "Adaugă un copil")}</h1>
          </div>
          <p className="text-white/70 text-[13px] mt-1.5">
            {t("addChild.subtitle", "Creează un cont pentru încă un copil și gestionează totul dintr-un singur loc.")}
          </p>
        </GameHero>

        <div className="px-4 pt-5">
          <GameCard className="p-5">
            {searchParams.get("status") === "cancel" && (
              <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                {t("addChild.payment_canceled", "Plata a fost anulată. Poți relua oricând.")}
              </div>
            )}

            {invitation && (
              <div className="mb-4 rounded-xl bg-indigo-50 border border-indigo-200 px-4 py-3 text-sm text-indigo-900">
                <p className="font-semibold">
                  {t("addChild.invited_by", "Invitație de la")}{" "}
                  {invitation.club?.public_name || invitation.club?.name}
                </p>
                <p className="mt-0.5 text-indigo-800">
                  {t("addChild.invited_team", "Copilul intră în echipa")}{" "}
                  <strong>{invitation.team?.name}</strong>
                  {invitation.team?.age_group ? ` (${invitation.team.age_group})` : ""}.
                </p>
                <p className="mt-1 text-xs text-indigo-700">
                  {t("addChild.invited_plan", "Planul este asigurat de club — nu ai nimic de plătit.")}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1.5 text-sm font-semibold text-slate-700">{t("addChild.first_name", "Prenumele copilului")}</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>

              <div>
                <label className="block mb-1.5 text-sm font-semibold text-slate-700">{t("addChild.age", "Vârsta")}</label>
                <select
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className={`w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${age ? "text-slate-800" : "text-slate-400"}`}
                >
                  <option value="" disabled>{t("addChild.age_placeholder", "Selectează vârsta")}</option>
                  {athleteAges(MAX_CHILD_AGE).map((n) => (
                    <option key={n} value={n} className="text-slate-800">
                      {t("addChild.age_years", "{{n}} ani", { n })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Identifier: username (no email) or email */}
              <div>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setIdMode("username")} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${idMode === "username" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300"}`}>
                    {t("addChild.by_username", "Username")}
                  </button>
                  <button type="button" onClick={() => setIdMode("email")} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${idMode === "email" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300"}`}>
                    {t("addChild.by_email", "Email")}
                  </button>
                </div>
                {idMode === "username" ? (
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t("addChild.username_ph", "ex: andrei.campion")} className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                ) : (
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplu.ro" className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                )}
                <p className="mt-1 text-xs text-slate-400">
                  {t("addChild.id_hint", "Copilul se poate autentifica cu username sau email.")}
                </p>
              </div>

              <div>
                <label className="block mb-1.5 text-sm font-semibold text-slate-700">{t("addChild.password", "Parola copilului")}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>

              {/* Plan picker — hidden on the invitation path: the club already
                  provides the plan, so there is nothing to choose or pay. */}
              <div className={invitation ? "hidden" : ""}>
                <label className="block mb-1.5 text-sm font-semibold text-slate-700">{t("addChild.plan", "Plan")}</label>
                <div className="space-y-2">
                  {loading ? (
                    <p className="text-sm text-slate-400">{t("common.loading", "Se încarcă...")}</p>
                  ) : (
                    plans.map((p) => {
                      const active = planCode === p.code;
                      const price = billing === "year" && p.price_cents_year != null ? p.price_cents_year : p.price_cents_month;
                      return (
                        <button
                          type="button"
                          key={p.code}
                          onClick={() => setPlanCode(p.code)}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition ${active ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white"}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-800">{p.name}</span>
                            <span className="text-sm font-semibold text-slate-700">
                              {p.is_free ? t("addChild.free", "Gratuit") : money(price, p.currency)}
                            </span>
                          </div>
                          {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Billing period toggle (paid plans with an annual option) */}
              {isPaid && selectedPlan?.price_cents_year != null && (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setBilling("month")} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${billing === "month" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300"}`}>
                    {t("addChild.monthly", "Lunar")}
                  </button>
                  <button type="button" onClick={() => setBilling("year")} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${billing === "year" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300"}`}>
                    {t("addChild.yearly", "Anual")}
                  </button>
                </div>
              )}

              {/* Sibling discount hint */}
              {isPaid && projectedDiscount > 0 && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
                  🎉 {t("addChild.discount_hint", "Cu acest copil ai {{n}} abonamente plătite — primești {{pct}}% reducere la TOATE abonamentele copiilor tăi.", { n: projectedPaid, pct: projectedDiscount })}
                </div>
              )}

              {error && <p className="text-sm text-rose-600">{error}</p>}

              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-bold rounded-xl transition"
              >
                {saving
                  ? t("addChild.saving", "Se procesează...")
                  : isPaid
                    ? t("addChild.continue_payment", "Continuă spre plată")
                    : t("addChild.create", "Creează cont")}
              </button>
            </form>
          </GameCard>
        </div>
      </div>
    </MobileAppLayout>
  );
}
